import { motion } from "framer-motion";

const EASE = [0.16, 1, 0.3, 1];

export const COMMANDMENTS = [
  {
    title: "HPA-Axis De-escalation",
    body: "Forcing cortisol and baseline serum adrenaline to absolute zero during strategic recovery.",
  },
  {
    title: "Endogenous GLP-1 Resynthesis",
    body: "Upregulating lipophilic signaling to trigger deep lipolysis and silence emotional hunger.",
  },
  {
    title: "Mitochondrial ATP Saturation",
    body: "Flooding the prefrontal cortex with bioavailable cell energy matrices to eliminate decision fatigue.",
  },
  {
    title: "Circadian Synchronicity",
    body: "Enforcing strict metabolic windows to match natural evolutionary gene expressions.",
  },
  {
    title: "Vagal Tone Mastery",
    body: "Activating deep interoceptive tracking to build unshakeable biometric resilience.",
  },
  {
    title: "The Epigenetic Nutritional Matrix",
    body: "Removing industrial inflammatory toxins and replacing them with high-voltage whole foods.",
  },
  {
    title: "Proteomic Age Reversal",
    body: "Suppressing low-grade systemic inflammation through strategic cellular autophagy.",
  },
  {
    title: "Transient Hypofrontality Access",
    body: "Calming the autonomic nervous system to unlock deep, uninterrupted Peak Flow States.",
  },
  {
    title: "Chronobiological Fasting Boundaries",
    body: "Respecting the 3-hour absolute pre-sleep deadline for optimal glymphatic clearance.",
  },
  {
    title: "Biological Autonomy Over Dependency",
    body: "Training the native cellular salvage pathways instead of relying on isolated synthetic chemical molecules.",
  },
];

export default function Commandments() {
  return (
    <section id="commandments" className="relative bg-ink py-24 sm:py-32 lg:py-40" data-testid="commandments-section">
      <div className="max-w-5xl mx-auto px-5 sm:px-8 lg:px-10">
        <motion.div
          initial={{ opacity: 0, y: 32 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: "-80px" }}
          transition={{ duration: 0.9, ease: EASE }}
          className="mb-16 sm:mb-24"
        >
          <p className="text-[11px] uppercase tracking-[0.35em] text-copper-hover mb-5" data-testid="commandments-eyebrow">
            The Core Pillars — The Joe Fenix Method
          </p>
          <h2 className="font-serif text-3xl sm:text-5xl lg:text-6xl text-gold font-medium tracking-tight leading-[1.12]" data-testid="commandments-heading">
            The 10 Commandments of<br className="hidden sm:block" /> Somatic Mastery
          </h2>
          <div className="gold-divider mt-8 sm:mt-10 max-w-md" />
          <p className="text-sm sm:text-base text-neutral-500 font-light mt-6 max-w-2xl leading-relaxed">
            Ten non-negotiable structural principles. Engineered, measured, and
            enforced — never negotiated with a depleted nervous system.
          </p>
        </motion.div>

        <div>
          {COMMANDMENTS.map((c, i) => (
            <motion.article
              key={i}
              data-testid={`commandment-item-${i + 1}`}
              initial={{ opacity: 0, y: 36 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, margin: "-60px" }}
              transition={{ duration: 0.8, delay: 0.05, ease: EASE }}
              className="group relative grid grid-cols-[auto_1fr] gap-5 sm:gap-10 py-8 sm:py-11 border-t border-gold/12 last:border-b hover:bg-ink-2/50 transition-colors duration-500 px-2 sm:px-6 -mx-2 sm:-mx-6"
            >
              <div className="font-serif text-4xl sm:text-6xl lg:text-7xl leading-none text-gold/25 group-hover:text-gold/70 transition-colors duration-500 select-none w-14 sm:w-24 text-right">
                {String(i + 1).padStart(2, "0")}
              </div>
              <div className="pt-1 sm:pt-2">
                <h3 className="font-sans font-bold text-white uppercase tracking-[0.14em] text-sm sm:text-lg lg:text-xl mb-2.5 sm:mb-3.5">
                  <span className="text-copper-hover mr-2 sm:mr-3 font-serif normal-case italic font-medium tracking-normal text-base sm:text-xl">
                    Principle {i + 1}
                  </span>
                  <span className="group-hover:text-gold-soft transition-colors duration-500">{c.title}</span>
                </h3>
                <p className="text-sm sm:text-base text-neutral-400 font-light leading-relaxed max-w-2xl">
                  {c.body}
                </p>
              </div>
              <span className="absolute left-0 top-0 h-full w-[2px] bg-copper scale-y-0 group-hover:scale-y-100 origin-top transition-transform duration-500" />
            </motion.article>
          ))}
        </div>
      </div>
    </section>
  );
}
